import { action, makeObservable } from 'mobx';

import { set } from 'lodash';

import { ProtectorType } from 'core/constants';

import FormBuilder from '../FormBuilder';
import { NEXT_SECTION } from './FormAction';

class SectionLogicStore {
  constructor(private formBuilder: FormBuilder) {
    makeObservable(this);
  }

  @action.bound
  addSection(sectionId?: string): string {
    if (!this.formBuilder.data) return '';

    const key = this.formBuilder.getNextContainerId();
    if (sectionId) {
      this.formBuilder.containers.splice(this.formBuilder.containers.indexOf(sectionId) + 1, 0, key);
    } else {
      this.formBuilder.containers.push(key);
    }

    const defaultNewSection = {
      actions: [],
      order: [],
      properties: {},
      required: [],
      title: 'Untitled section',
      description: '',
    };

    this.formBuilder.data.order = this.formBuilder.containers;
    this.formBuilder.data.properties[key] = defaultNewSection;

    this.formBuilder.dataManager.transformDataForDisplay();
    return key;
  }

  @action.bound
  addQuestionToSection(sectionId: string): void {
    if (!this.formBuilder.data) return;

    const key = this.formBuilder.getNextContainerId();
    this.formBuilder.items = {
      ...this.formBuilder.items,
      [sectionId]: [...this.formBuilder.items[sectionId], key],
    };

    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.order`,
      this.formBuilder.items[sectionId]
    );

    const defaultNewField = {
      description: 'Untitled question',
      questionType: ProtectorType.SingleChoice,
      properties: {
        enum: [
          {
            key: this.formBuilder.getNextContainerId(),
            value: 'Untitled 1',
          },
        ],
      },
    };
    this.formBuilder.data = set(
      { ...this.formBuilder.data },
      `properties.${sectionId}.properties.${key}`,
      defaultNewField
    );

    this.formBuilder.setCurrentEditedQuestion({ sectionId, fieldId: key });
  }

  @action.bound
  removeSection(key: string): void {
    this.formBuilder.containers = this.formBuilder.containers.filter((container) => container !== key);

    // update actions
    this.formBuilder.formAction.removeSectionActions(key);
    this.formBuilder.formAction.removeDefaultSectionAction(key);
  }

  @action.bound
  getSectionTitleAndDescription(id: string): { title: string; description: string } {
    if (!this.formBuilder.data) return { title: '', description: '' };
    return {
      title: this.formBuilder.data.properties[id].title,
      description: this.formBuilder.data.properties[id].description || '',
    };
  }

  @action.bound
  updateSectionDetails(id: string, title?: string, description?: string): void {
    if (!this.formBuilder.data) return;
    if (title !== undefined) {
      this.formBuilder.data = set({ ...this.formBuilder.data }, `properties.${id}.title`, title);
    }
    if (description !== undefined) {
      this.formBuilder.data = set({ ...this.formBuilder.data }, `properties.${id}.description`, description);
    }
  }

  @action.bound
  updateSectionOrder(order: string[]): void {
    if (!this.formBuilder.data) return;

    this.formBuilder.containers = order;
    this.formBuilder.data = set({ ...this.formBuilder.data }, 'order', order);

    // After reordering:
    // All “Continue to next section” logic must respect the new order.
    // If logic points to a section that is now earlier in the flow (backward jump),
    // this logic should be invalidated and reset to "Continue to next section."

    this.formBuilder.containers.forEach((sectionKey) => {
      const actions = this.formBuilder.formAction.formActions[sectionKey];
      if (!actions) return;
      actions.forEach((a) => {
        // check only actions with nextSection
        if ('nextSection' in a.then && order.indexOf(a.then.nextSection) < order.indexOf(sectionKey)) {
          if (typeof a.when === 'boolean') {
            // default action at the end of the section
            this.formBuilder.formAction.setSectionDefaultAction(sectionKey, NEXT_SECTION);
          } else {
            this.formBuilder.formAction.updateFormActions(
              sectionKey,
              a.when.equals[0],
              a.when.equals[1][0],
              NEXT_SECTION
            );
          }
        }
      });
    });
  }
}

export default SectionLogicStore;
